import { useEffect, useMemo, useRef, useState } from 'react';
import { DiceEvent, MoveEvent, type BoardHighlight, type BoardToken, type BoardView, type SelfInfo, type ServerEventMessage } from '@momentum/shared';
import { CharacterBadge } from './CharacterBadge.tsx';
import { fitPointsToBox, pathPositionAt, ringLayout, type Point2D } from './layout.ts';

export interface Board2DProps {
  view: BoardView;
  you: SelfInfo | null;
  onEvent: (listener: (event: ServerEventMessage) => void) => () => void;
  reducedMotion: boolean;
}

const VIEWBOX_W = 340;
const VIEWBOX_H = 260;
const NODE_R = 20;
const PADDING = NODE_R + 18;
const HOP_MS = 600;
const DICE_TOAST_MS = 1_500;

function edgeGeometry(a: Point2D, b: Point2D, nodeR: number) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.hypot(dx, dy) || 1;
  const ux = dx / dist;
  const uy = dy / dist;
  const start = { x: a.x + ux * nodeR, y: a.y + uy * nodeR };
  const end = { x: b.x - ux * nodeR, y: b.y - uy * nodeR };
  const arrowLen = 9;
  const arrowWidth = 5;
  const backX = end.x - ux * arrowLen;
  const backY = end.y - uy * arrowLen;
  const perpX = -uy * arrowWidth;
  const perpY = ux * arrowWidth;
  const arrowPoints = `${end.x},${end.y} ${backX + perpX},${backY + perpY} ${backX - perpX},${backY - perpY}`;
  return { start, end: { x: backX, y: backY }, arrowPoints };
}

/**
 * SVG rendering of a `BoardView`. No canonical state lives here: `view.tokens[].spaceId` is always the source
 * of truth, and local `board.move`/`board.dice` animation is a purely visual overlay that never blocks the
 * next authorised snapshot from applying. Presentational (`aria-hidden`); the accessible summary lives on the
 * wrapping `role="img"` container, and controls (real buttons) live in the sibling `BoardPanel`.
 */
export function Board2D({ view, you, onEvent, reducedMotion }: Board2DProps) {
  const youId = you?.participantId ?? null;

  const spaceIds = useMemo(() => Object.keys(view.graph.spaces), [view.graph]);

  const screenPositions = useMemo(() => {
    const allPositioned = spaceIds.every((id) => view.graph.spaces[id].position !== null);
    const raw: Record<string, Point2D> = allPositioned
      ? Object.fromEntries(spaceIds.map((id) => {
          const p = view.graph.spaces[id].position!;
          return [id, { x: p.x, y: p.z }];
        }))
      : ringLayout(spaceIds, { radius: 1 });
    return fitPointsToBox(raw, { width: VIEWBOX_W, height: VIEWBOX_H, padding: PADDING });
  }, [spaceIds, view.graph]);

  const highlightsBySpace = useMemo(() => {
    const map = new Map<string, BoardHighlight['kind'][]>();
    for (const h of view.highlights) {
      const list = map.get(h.spaceId) ?? [];
      list.push(h.kind);
      map.set(h.spaceId, list);
    }
    return map;
  }, [view.highlights]);

  const stackOffsetsBySpace = useMemo(() => {
    const grouped = new Map<string, string[]>();
    for (const t of view.tokens) {
      const list = grouped.get(t.spaceId) ?? [];
      list.push(t.participantId);
      grouped.set(t.spaceId, list);
    }
    const map = new Map<string, Record<string, Point2D>>();
    for (const [spaceId, ids] of grouped) {
      map.set(spaceId, ringLayout(ids, { radius: ids.length > 1 ? 12 : 0 }));
    }
    return map;
  }, [view.tokens]);

  // --- local animation of board.move / board.dice (visual only; canonical state is always view.tokens) ---
  const screenPositionsRef = useRef(screenPositions);
  screenPositionsRef.current = screenPositions;
  const animsRef = useRef<Record<string, { sequence: Point2D[]; startedAt: number; totalMs: number }>>({});
  const rafRef = useRef<number | null>(null);
  const diceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [animPositions, setAnimPositions] = useState<Record<string, Point2D>>({});
  const [diceToast, setDiceToast] = useState<{ participantId: string; value: number } | null>(null);

  useEffect(() => {
    function tick() {
      const now = performance.now();
      const next: Record<string, Point2D> = {};
      let anyActive = false;
      for (const pid of Object.keys(animsRef.current)) {
        const anim = animsRef.current[pid];
        const { position, done } = pathPositionAt(anim.sequence, now - anim.startedAt, HOP_MS);
        if (done) {
          delete animsRef.current[pid];
        } else {
          next[pid] = position;
          anyActive = true;
        }
      }
      setAnimPositions(next);
      rafRef.current = anyActive ? requestAnimationFrame(tick) : null;
    }

    // If `onEvent` is a fresh function identity every render, this effect resubscribes; resume any
    // in-flight animation loop rather than losing it (animsRef survives resubscription — it's a ref).
    if (Object.keys(animsRef.current).length > 0 && rafRef.current === null) {
      rafRef.current = requestAnimationFrame(tick);
    }

    const unsubscribe = onEvent((event) => {
      if (event.name === 'board.move') {
        if (reducedMotion) return; // canonical position is applied immediately on the next state frame
        const parsed = MoveEvent.safeParse(event.payload);
        if (!parsed.success) return;
        const move = parsed.data;
        const positions = screenPositionsRef.current;
        const startPos = positions[move.from] ?? positions[move.to];
        if (!startPos) return;
        const sequence: Point2D[] = [startPos, ...move.path.map((id) => positions[id] ?? startPos)];
        animsRef.current = {
          ...animsRef.current,
          [move.participantId]: { sequence, startedAt: performance.now(), totalMs: Math.max(move.path.length, 1) * HOP_MS },
        };
        if (rafRef.current === null) rafRef.current = requestAnimationFrame(tick);
      } else if (event.name === 'board.dice') {
        const parsed = DiceEvent.safeParse(event.payload);
        if (!parsed.success) return;
        if (diceTimerRef.current) clearTimeout(diceTimerRef.current);
        setDiceToast({ participantId: parsed.data.participantId, value: parsed.data.value });
        diceTimerRef.current = setTimeout(() => setDiceToast(null), DICE_TOAST_MS);
      }
    });

    return () => {
      unsubscribe();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (diceTimerRef.current) clearTimeout(diceTimerRef.current);
    };
  }, [onEvent, reducedMotion]);

  function spaceLabel(id: string): string {
    return view.graph.spaces[id]?.label ?? id;
  }
  function participantName(id: string): string {
    if (id === youId) return 'You';
    return view.tokens.find((t) => t.participantId === id)?.displayName ?? id;
  }

  const legalMoves = Array.from(highlightsBySpace.entries())
    .filter(([, kinds]) => kinds.includes('legal-move'))
    .map(([id]) => spaceLabel(id));
  const launchSpaces = Array.from(highlightsBySpace.entries())
    .filter(([, kinds]) => kinds.includes('launch'))
    .map(([id]) => spaceLabel(id));
  const destinationSpaces = Array.from(highlightsBySpace.entries())
    .filter(([, kinds]) => kinds.includes('destination'))
    .map(([id]) => spaceLabel(id));

  const tokenSummary = view.tokens
    .map((t) => `${participantName(t.participantId)} at ${spaceLabel(t.spaceId)}${t.connected ? '' : ' (disconnected)'}`)
    .join('; ');
  const summaryParts = [`Board with ${spaceIds.length} spaces. Tokens: ${tokenSummary || 'none'}.`];
  if (legalMoves.length > 0) summaryParts.push(`Legal moves from your position: ${legalMoves.join(', ')}.`);
  if (launchSpaces.length > 0) summaryParts.push(`Activity spaces: ${launchSpaces.join(', ')}.`);
  if (destinationSpaces.length > 0) summaryParts.push(`Destination: ${destinationSpaces.join(', ')}.`);
  const boardSummary = summaryParts.join(' ');

  return (
    <div>
      <div role="img" aria-label={boardSummary}>
        <svg
          viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
          width="100%"
          style={{ maxWidth: 480, display: 'block' }}
          aria-hidden="true"
        >
          {spaceIds.map((fromId) => {
            const from = screenPositions[fromId];
            const space = view.graph.spaces[fromId];
            return space.connections.map((toId) => {
              const to = screenPositions[toId];
              if (!from || !to) return null;
              const geo = edgeGeometry(from, to, NODE_R);
              return (
                <g key={`${fromId}->${toId}`}>
                  <line x1={geo.start.x} y1={geo.start.y} x2={geo.end.x} y2={geo.end.y} stroke="var(--line, #e2e5ea)" strokeWidth={2} />
                  <polygon points={geo.arrowPoints} fill="var(--line, #e2e5ea)" />
                </g>
              );
            });
          })}

          {spaceIds.map((id) => {
            const pos = screenPositions[id];
            if (!pos) return null;
            const space = view.graph.spaces[id];
            const kinds = highlightsBySpace.get(id) ?? [];
            const isActivity = space.kind === 'activity';
            return (
              <g key={id} transform={`translate(${pos.x} ${pos.y})`}>
                {kinds.includes('legal-move') && (
                  <circle r={NODE_R + 6} fill="none" stroke="var(--accent, #1a73ff)" strokeWidth={2} strokeDasharray="4 3" />
                )}
                {kinds.includes('launch') && (
                  <circle r={NODE_R + 10} fill="none" stroke="var(--highlight, #ffcc00)" strokeWidth={2.5} />
                )}
                {kinds.includes('destination') && (
                  <circle r={NODE_R + 4} fill="none" stroke="var(--success, #2e9e5b)" strokeWidth={2} />
                )}
                {isActivity ? (
                  <rect x={-NODE_R} y={-NODE_R} width={NODE_R * 2} height={NODE_R * 2} rx={7} fill="var(--surface, #f6f7f9)" stroke="var(--ink, #111318)" strokeWidth={1.5} />
                ) : (
                  <circle r={NODE_R} fill="var(--surface, #f6f7f9)" stroke="var(--ink, #111318)" strokeWidth={1.5} />
                )}
                {kinds.includes('marker') && <circle cx={NODE_R - 5} cy={-(NODE_R - 5)} r={4} fill="var(--ink-muted, #5b6270)" />}
                <text y={NODE_R + 14} textAnchor="middle" fontSize={11} fill="var(--ink, #111318)">
                  {space.label ?? id}
                </text>
                {space.kind && space.kind !== 'plain' && (
                  <text y={NODE_R + 26} textAnchor="middle" fontSize={9} fill="var(--ink-muted, #5b6270)">
                    {space.kind}
                  </text>
                )}
              </g>
            );
          })}

          {view.tokens.map((token: BoardToken) => {
            const canonical = screenPositions[token.spaceId] ?? { x: VIEWBOX_W / 2, y: VIEWBOX_H / 2 };
            const animated = animPositions[token.participantId];
            const base = animated ?? canonical;
            const stackOffset = stackOffsetsBySpace.get(token.spaceId)?.[token.participantId] ?? { x: 0, y: 0 };
            const x = base.x + stackOffset.x;
            const y = base.y + stackOffset.y;
            const emphasis = token.participantId === youId ? 'you' : token.isTurnHolder ? 'turn' : 'none';
            return (
              <g key={token.participantId} transform={`translate(${x - 14} ${y - 14})`} opacity={token.connected ? 1 : 0.5}>
                <CharacterBadge slot={token.characterSlot} size={28} title={token.displayName} emphasis={emphasis} />
              </g>
            );
          })}
        </svg>
      </div>
      <div aria-live="polite" style={{ minHeight: '1.2em', fontSize: '0.9em' }}>
        {diceToast ? `${participantName(diceToast.participantId)} rolled ${diceToast.value}.` : ''}
      </div>
    </div>
  );
}
